import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "@/components/layout/Footer";

import MarketplaceHeader from "@/components/layout/MarketplaceHeader";
import MobileBottomNav from "@/components/layout/MobileBottomNav";
import CookieBanner from "@/components/layout/CookieBanner";
import UtilityBar from "@/components/layout/UtilityBar";
import MainNav from "@/components/layout/MainNav";
import HeroBento from "@/components/home/HeroBento";
import VendorSocialProofStrip from "@/components/home/VendorSocialProofStrip";
import TrustBar from "@/components/home/TrustBar";
import ComingSoonModal from "@/components/home/ComingSoonModal";
import LocalBusinessSchema from "@/components/seo/LocalBusinessSchema";
import CanonicalLink from "@/components/seo/CanonicalLink";
import GoogleAnalytics from "@/components/analytics/GoogleAnalytics";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy load below-the-fold components
const ExploreCategories = lazy(() => import("@/components/home/ExploreCategories"));
const TrendingProducts = lazy(() => import("@/components/home/TrendingProducts"));
const MarketplaceCTA = lazy(() => import("@/components/home/MarketplaceCTA"));
const TopSellingProducts = lazy(() => import("@/components/home/TopSellingProducts"));
const PromoBanners = lazy(() => import("@/components/home/PromoBanners"));
const FeaturedBrands = lazy(() => import("@/components/home/FeaturedBrands"));
const ShopTheLookHome = lazy(() => import("@/components/home/ShopTheLookHome"));
const ValueProps = lazy(() => import("@/components/home/ValueProps"));
const QualityMarquee = lazy(() => import("@/components/home/QualityMarquee"));
const CommunityEngagement = lazy(() => import("@/components/home/CommunityEngagement"));
const HomepageFAQ = lazy(() => import("@/components/home/HomepageFAQ"));

const SectionSkeleton = () => (
  <div className="py-12 container mx-auto px-4">
    <Skeleton className="h-8 w-64 mb-6" />
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="aspect-square rounded-2xl" />
      ))}
    </div>
  </div>
);

const Index = () => {
  const { isAdmin } = useAuth();

  return (
    <div className="min-h-screen flex flex-col fabric-texture relative">
      <Helmet>
        <title>Hijabs, Abayas & Modest Fashion — HijabiStore USA</title>
        <meta name="description" content="America's online hijab store. Shop 500+ hijabs, abayas, modest dresses & Islamic clothing from independent designers. Free US shipping over $75." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://hijabistore.com/" />
        <meta property="og:title" content="Hijabs, Abayas & Modest Fashion — HijabiStore USA" />
        <meta property="og:description" content="America's online hijab store. Shop 500+ hijabs, abayas, modest dresses & Islamic clothing from independent designers. Free US shipping over $75." />
        <meta property="og:url" content="https://hijabistore.com/" />
        <meta property="og:image" content="https://hijabistore.com/images/hero-new-arrivals.jpg" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="HijabiStore" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Hijabs, Abayas & Modest Fashion — HijabiStore USA" />
        <meta name="twitter:description" content="America's online hijab store. Shop 500+ hijabs, abayas, modest dresses & Islamic clothing from independent designers. Free US shipping over $75." />
        <meta name="twitter:image" content="https://hijabistore.com/images/hero-new-arrivals.jpg" />
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "HijabiStore",
          "url": "https://hijabistore.com",
          "description": "Modest fashion marketplace for hijabs, abayas, Islamic clothing and modest activewear",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://hijabistore.com/shop?q={query}",
            "query-input": "required name=query"
          }
        }`}</script>
      </Helmet>
      <LocalBusinessSchema />
      <GoogleAnalytics />
      <ComingSoonModal />

      {isAdmin && (
        <div className="bg-primary text-primary-foreground py-1 md:py-1.5 px-3 md:px-4 text-center text-[10px] md:text-xs">
          <Link to="/admin" className="hover:underline inline-flex items-center gap-1.5 md:gap-2">
            🔐 <span className="hidden sm:inline">Admin Panel — Manage vendors, users, and platform settings</span>
            <span className="sm:hidden">Admin Panel</span>
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      )}

      <UtilityBar />
      <MarketplaceHeader />
      <MainNav />

      <main id="main-content" className="flex-1 pb-16 md:pb-0">
        <h1 className="sr-only">HijabiStore — America's Hijabi Shop & Modest Fashion Marketplace for Hijabs, Abayas & Islamic Clothing</h1>
        <HeroBento />
        <TrustBar />
        <VendorSocialProofStrip />

        <Suspense fallback={<SectionSkeleton />}>
          <ExploreCategories />
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <TrendingProducts />
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <MarketplaceCTA />
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <TopSellingProducts />
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <PromoBanners />
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <FeaturedBrands />
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <ShopTheLookHome />
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <ValueProps />
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <HomepageFAQ />
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <QualityMarquee />
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <CommunityEngagement />
        </Suspense>
      </main>

      <Footer />
      <MobileBottomNav />
      <CookieBanner />
    </div>
  );
};

export default Index;
